import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Info } from "lucide-react";
import { useEffect, useState } from "react";

import DOBCalendar from "@/pages/components/dob-calendar";
import { cn } from "@/lib/utils";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import { formatDateToMMDDYYYY } from "@/lib/date-utils";

// export const formatDateToMMDDYYYY = (date?: Date): string | undefined => {
//   if (!date) return undefined;
//   const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
//   const day = String(date.getDate()).padStart(2, "0");
//   const year = date.getFullYear();
//   return `${month}/${day}/${year}`;
// };

type DatePickerProps = {
  id: string;
  label: string;
  placeholder: string;
  value: Date | undefined;
  onChange?: (date: Date | undefined) => void;
  disabled?: boolean;
  error?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  onClose?: () => void;
};

const InputDate = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  disabled = false,
  error,
  className,
  minDate,
  maxDate,
  onClose = () => {},
}: DatePickerProps) => {
  const [date, setDate] = useState<Date>();
  const [isOpen, setIsOpen] = useState(false);

  const handlePopupClose = (nextState: boolean) => {
    onClose();
    setIsOpen(nextState);
  };

  useEffect(() => {
    setDate(value);
  }, [value]);

  return (
    <div className="relative grid w-full items-center gap-3">
      <Label htmlFor={id}>{label}</Label>
      {/* Hidden input so DOB participates in form context logging (uses id as key). */}
      <input
        type="hidden"
        id={id}
        name={id}
        value={date ? (formatDateToMMDDYYYY(date) ?? "") : ""}
        readOnly
      />
      {error && (
        <HoverCard>
          <HoverCardTrigger asChild>
            <Info
              className="absolute bottom-3.5 right-2 cursor-pointer text-[11px] text-red-400"
              size={16}
            />
          </HoverCardTrigger>
          <HoverCardContent>
            <em className="text-xs text-red-400">{error}</em>
          </HoverCardContent>
        </HoverCard>
      )}
      <Popover modal={true} open={isOpen} onOpenChange={handlePopupClose}>
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            variant="outline"
            className={cn(
              "flex h-11 w-full justify-between rounded-md border-none bg-muted px-3 py-2 text-medium font-normal text-foreground ring-offset-background hover:text-black focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus-visible:outline-none focus-visible:ring-ring",
              className,
              !date && "text-foreground/50",
            )}
          >
            <div className="flex items-center">
              <CalendarIcon className="mb-1 mr-2 size-4 text-muted-foreground" />
              {date ? (
                <span>{formatDateToMMDDYYYY(date)}</span>
              ) : (
                <span
                  className={cn("text-medium font-normal text-black/40")}
                >{`${placeholder}`}</span>
              )}
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="z-[150] flex w-auto flex-col items-center justify-center p-0"
          align="start"
        >
          <div className="flex space-x-2 p-3">
            <DOBCalendar
              onDateChange={(newDate) => {
                setDate(newDate);
                onChange?.(newDate);
                setIsOpen(false);
              }}
              selectedDate={date}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default InputDate;
